<section class="transcriber-content">
  <ol class="phase-rail">
    @for (phaseStep of phases; track phaseStep; let i = $index) {
      <li [class.current]="phase === phaseStep" [class.done]="isPhaseDone(phaseStep)">
        <!--
          Een eerdere fase blijft bereikbaar om terug te kijken; verder dan je bent gekomen
          kun je niet springen, want die fase heeft nog geen inhoud.
        -->
        <button
          type="button"
          class="phase-link"
          [disabled]="!isPhaseReachable(phaseStep)"
          (click)="goToPhase(phaseStep)"
        >
          <span class="marker">
            @if (isPhaseDone(phaseStep)) {
              <mat-icon>check</mat-icon>
            } @else {
              {{ i + 1 }}
            }
          </span>
          {{ phaseTranslationKeyPrefix + phaseStep | translate }}
        </button>
      </li>
    }
  </ol>

  @if (configError) {
    <app-inline-snackbar
      [type]="SnackbarType.ERROR"
      [message]="'transcriber.configError' | translate"
      [showCloseButton]="false"
    ></app-inline-snackbar>
  }

  @if (templatesError) {
    <app-inline-snackbar
      [type]="SnackbarType.ERROR"
      [message]="'transcriber.templatesError' | translate"
      [showCloseButton]="false"
    ></app-inline-snackbar>
  }

  @if (phase === TranscriberPhaseEnum.SETUP) {
    @if (isReadOnly) {
      <!--
        Terugkijken mag, wijzigen niet: de keuzes hieronder horen bij het gesprek dat al
        loopt. Wie andere keuzes wil, begint een nieuw gesprek.
      -->
      <div class="setup-fields readonly-fields">
        <p class="phase-hint">{{ 'transcriber.setupReadOnly' | translate }}</p>

        <dl>
          <dt>{{ 'transcriber.dossierGroup' | translate }}</dt>
          <dd>{{ workProcessLabel || ('transcriber.notChosen' | translate) }}</dd>

          <dt>{{ 'transcriber.conversationTypeLabel' | translate }}</dt>
          <dd>
            @if (selectedConversationType) {
              {{ selectedConversationType.label }}
            } @else {
              {{ 'transcriber.notChosen' | translate }}
            }
          </dd>
        </dl>
      </div>

      <div class="phase-actions">
        <button mat-flat-button (click)="returnToCurrentPhase()">
          {{ 'button.backToPhase' | translate: { phase: furthestPhaseLabel | translate } }}
        </button>

        <button mat-stroked-button (click)="startNewConversation()">
          {{ 'button.newConversation' | translate }}
        </button>
      </div>
    } @else {
      <div class="setup-fields">
        <h3 class="field-group-title">{{ 'transcriber.dossierGroup' | translate }}</h3>
        <app-work-process-selector
          [preselected]="selectedWorkProcess"
          (workProcessSelected)="onWorkProcessSelected($event)"
        ></app-work-process-selector>

        <h3 class="field-group-title">{{ 'transcriber.conversationGroup' | translate }}</h3>
        <p class="field-group-hint">{{ 'transcriber.conversationTypeHint' | translate }}</p>

        <section class="transcriber-options">
          <mat-form-field appearance="outline">
            <mat-label>{{ 'transcriber.selectConversationType' | translate }}</mat-label>
            <mat-select
              name="conversationType"
              #conversationTypeSelect="ngModel"
              required
              [(ngModel)]="selectedConversationType"
              (selectionChange)="onDropdownChange()"
            >
              @for (conversationType of conversationTypes; track conversationType.task) {
                <mat-option [value]="conversationType">
                  {{ conversationType.label }}
                </mat-option>
              }
            </mat-select>

            @if (conversationTypeSelect.invalid && conversationTypeSelect.touched) {
              <mat-error> {{ 'transcriber.selectConversationTypeRequired' | translate }} </mat-error>
            }
          </mat-form-field>
        </section>
      </div>

      <div class="phase-actions">
        @if (!canStartConversation) {
          <p class="phase-hint">{{ 'transcriber.conversationTypeRequiredToStart' | translate }}</p>
        }

        <button
          mat-flat-button
          [disabled]="!canStartConversation"
          [class.disabled]="!canStartConversation"
          (click)="goToRecording()"
        >
          {{ 'button.startConversation' | translate }}
        </button>
      </div>
    }
  }

  @if (phase === TranscriberPhaseEnum.RECORDING) {
    <div class="conversation-context">
      @if (selectedConversationType) {
        <span>
          {{ selectedConversationType.label }}
        </span>
      }
      @if (workProcessLabel) {
        <span class="separator">·</span>
        <span>{{ workProcessLabel }}</span>
      }
    </div>
  }

  <!--
    De widget wordt pas bij het gesprek aangemaakt (zie isTranscriberMounted) en blijft daarna
    gemount buiten de gespreksfase: opnieuw aanmaken zou de opname en het transcript
    weggooien. Bij terugkijken blijft hij verborgen, zodat een afgerond gesprek niet per
    ongeluk opnieuw start.
  -->
  <div
    [class.transcriber-disabled]="!selectedConversationType"
    [class.transcriber-hidden]="phase !== TranscriberPhaseEnum.RECORDING || isReadOnly"
  >
    @if (xApiKey && isTranscriberMounted) {
      <mynte-transcriber
        #transcriberRef
        [apiPath]="apiPath"
        [xApiKey]="xApiKey"
        [assistantId]="assistantId"
        [tenantId]="tenantId"
        (transcribeEvent)="onTranscribeEvent($event)"
      >
      </mynte-transcriber>
    }
  </div>

  @if (phase === TranscriberPhaseEnum.RECORDING) {
    @if (isReadOnly) {
      <div class="summary transcript-review">
        <div class="summary-header">
          <h3>{{ 'transcriber.transcript' | translate }}</h3>
          @for (segment of transcriptSegments; track $index) {
            <p><strong>{{ segment.speaker }}:</strong> {{ segment.text }}</p>
          } @empty {
            <!-- Een aangeleverd transcript heeft geen segmenten om terug te lezen; dan is
                 het bestand zelf wat er te zien valt. -->
            @if (uploadedTranscript) {
              <p>{{ 'transcriber.transcriptDocument' | translate }} {{ uploadedTranscript.name }}</p>
            } @else {
              <p class="phase-hint">{{ 'transcriber.noTranscript' | translate }}</p>
            }
          }
        </div>
      </div>

      <div class="phase-actions">
        <button mat-flat-button (click)="returnToCurrentPhase()">
          {{ 'button.backToPhase' | translate: { phase: furthestPhaseLabel | translate } }}
        </button>
      </div>
    } @else {
      <!--
        Voor testdoeleinden: in plaats van een gesprek opnemen kun je hier een bestaand
        transcript aanleveren. Zodra de opname loopt verdwijnt deze mogelijkheid — dan gaat
        het gesprek verder met wat er opgenomen wordt.
      -->
      @if (canUploadTranscript) {
        <div class="transcript-upload">
          <p class="phase-hint">{{ 'transcriber.uploadTranscriptHint' | translate }}</p>

          <app-upload-document
            [allowOptionalFiles]="false"
            [documentType]="DocumentTypeEnum.TRANSCRIPT"
          ></app-upload-document>
        </div>
      }

      @if (canReturnToSetup || hasUploadedTranscript) {
        <div class="phase-actions">
          @if (canReturnToSetup) {
            <button mat-stroked-button (click)="backToSetup()">
              {{ 'button.backToSetup' | translate }}
            </button>
          }

          @if (hasUploadedTranscript) {
            <button mat-flat-button (click)="useUploadedTranscript()">
              {{ 'button.continueToSummary' | translate }}
            </button>
          }
        </div>
      }
    }
  }

  @if (phase === TranscriberPhaseEnum.SUMMARY) {
    @if (isSummarizing) {
      <div class="summarizing">
        <app-waiting [message]="'transcriber.summarizing' | translate"></app-waiting>
        <button mat-stroked-button (click)="previewSummary()">
          {{ 'documentEditor.preview' | translate }}
        </button>
      </div>
    }
    @if (summaryError) {
      <app-inline-snackbar
        [type]="SnackbarType.ERROR"
        [message]="'transcriber.summaryError' | translate"
        [showCloseButton]="false"
      ></app-inline-snackbar>
    }
    @if (summaryFile) {
      <div class="summary">
        <div class="summary-header">
          <h3>{{ 'transcriber.summary' | translate }}</h3>
        </div>

        <!--
          Het gespreksverslag gaat als document naar Suites, dus corrigeren gebeurt in het
          document zelf: wat je hier goedkeurt is precies wat er straks in het dossier staat.
          Na het genereren opent de editor vanzelf; deze knop opent hem opnieuw.
        -->
        <div class="summary-review">
          <p>{{ 'transcriber.checkDescription' | translate }}</p>

          <button mat-stroked-button (click)="previewSummary()">
            {{ 'documentEditor.preview' | translate }}
          </button>
        </div>

        <!--
          Ook zonder dossier kun je opnemen en samenvatten; het is pas nodig om weg te
          schrijven. Daarom staat de kiezer hier, net als bij de verifieer- en
          beschikkingstappen — anders loop je hier vast zonder uitweg.
        -->
        <app-work-process-selector
          [preselected]="selectedWorkProcess"
          (workProcessSelected)="onWorkProcessSelected($event)"
        ></app-work-process-selector>

        <div class="action-buttons">
          <button
            mat-stroked-button
            [disabled]="isSummaryUploadDisabled"
            [class.disabled]="isSummaryUploadDisabled"
            (click)="uploadSummary()"
          >
            {{ 'button.uploadToSsd' | translate }}
          </button>

          <button mat-stroked-button (click)="startNewConversation()">
            {{ 'button.newConversation' | translate }}
          </button>
        </div>
      </div>
    }
    @if (hasNoAudioWarning) {
      <div class="no-audio-warning">
        <p>{{ 'transcriber.noAudioDetected' | translate }}</p>

        <button mat-flat-button (click)="refreshPage()">
          {{ 'transcriber.refreshPage' | translate }}
        </button>
      </div>
    }
  }
</section>
